<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import Card from 'primevue/card'
import FormSection from '@/components/forms/FormSection.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import InputNumber from 'primevue/inputnumber'
import { yarnType } from '@/constants/yarns'
import { getSupplierList } from '@/api/suppliers'
import type { Supplier } from '@/types/supplier-types'
import InputLabel from '@/components/inputs/InputLabel.vue'
import {CurrencyType} from "@/types/currency-types";
import type { Yarn } from '@/types/yarn-types'
import { RouterLink } from 'vue-router'

const props = defineProps<{
  values: Yarn
  type: 'add' | 'edit' | 'view'
  onSubmit: () => void
  setErrors: (errors: any) => void
}>()

const suppliers = ref<Supplier[] | undefined>()
const totalCostAmount = ref(props.values.totalCostAmount)

onMounted(async () => {
  suppliers.value = await getSupplierList()
})

const isAddPage = computed(() => props.type === 'add')
const isViewPage = computed(() => props.type === 'view')
watch(
  () => [props.values.costPerKgAmount, props.values.totalKgOrdered],
  ([costPerKgAmount, totalKgOrdered]) => {
    if (costPerKgAmount && totalKgOrdered) {
      totalCostAmount.value = costPerKgAmount * totalKgOrdered;
    } else {
      totalCostAmount.value = 0;
    }
  }
);

watch(() => props.values.costPerKgCurrency, (newCurrency) => {
  props.values.totalCostCurrency = newCurrency
})

watch(
  () => props.values.costPerKgCurrency,
  (newCurrency) => {
    if (newCurrency) {
      props.values.totalCostCurrency = newCurrency;
    }
  }
);
</script>

<template>
  <form @submit.prevent="onSubmit" class="border-t border-neutral-200/50 !rounded-t-3xl pb-2">
    <Card>
      <template #content>
        <div>
          <FormSection title="Merge Details">
            <FormInputText
              :viewOnly="isViewPage"
              label="Merge Number"
              inputName="mergeNumber"
              id="mergeNumber"
            />

            <div>
              <template v-if="isViewPage">
                <InputLabel>Supplier</InputLabel>
                <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                  <RouterLink
                    :to="`/suppliers/${values.supplierId}`"
                    class="font-medium text-neutral-800 hover:underline"
                  >
                    {{ suppliers?.find((supplier) => supplier.id === values.supplierId)?.name }}
                  </RouterLink>
                </p>
              </template>
              <template v-else>
                <FormDropdown
                  label="Supplier"
                  inputName="supplierId"
                  id="supplierId"
                  :options="suppliers"
                  optionLabel="name"
                  optionValue="id"
                />
              </template>
            </div>

            <div>
              <template v-if="isViewPage">
                <InputLabel>Yarn Type</InputLabel>
                <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                  {{ yarnType.find((type) => type.id === values.yarnType)?.name }}
                </p>
              </template>
              <template v-else>
                <FormDropdown
                  label="Yarn Type"
                  inputName="yarnType"
                  id="yarnType"
                  :options="yarnType"
                  optionLabel="name"
                  optionValue="id"
                  :viewOnly="isViewPage"
                />
              </template>
            </div>

            <FormInputText
              :viewOnly="isViewPage"
              label="Yarn Colour"
              inputName="yarnColour"
              id="yarnColour"
              @blur="values.yarnColour = values.yarnColour?.trim()"
            />
            <FormInputText
              :viewOnly="isViewPage"
              label="Kilograms Available"
              inputName="kgAvailable"
              id="kgAvailable"
              type="number"
            />
            <FormInputText
              :viewOnly="isViewPage"
              label="Total Kilograms Ordered"
              inputName="totalKgOrdered"
              id="totalKgOrdered"
              type="number"
            />

            <div class="flex w-full items-center gap-2">
              <div class="w-1/4">
                <FormDropdown
                  label="Currency"
                  :viewOnly="isViewPage"
                  inputName="costPerKgCurrency"
                  id="costPerKgCurrency"
                  :options="CurrencyType"
                  optionLabel="symbol"
                  optionValue="id"
                  type="number"
                />
              </div>
              <div class="flex flex-col w-full">
                <FormInputText
                  :viewOnly="isViewPage"
                  label="Cost per Kilo"
                  inputName="costPerKgAmount"
                  id="costPerKgAmount"
                  type="number"
                />
              </div>
            </div>
            <div class="flex w-full items-center gap-2">
              <div class="w-1/4">
                <FormDropdown
                  label="Currency"
                  :viewOnly="true"
                  inputName="costPerKgCurrency"
                  id="costPerKgCurrency"
                  :options="CurrencyType"
                  optionLabel="symbol"
                  optionValue="id"
                  type="number"
                  v-model="values.costPerKgCurrency"
                  @update:modelValue="values.totalCostCurrency = $event"
                />
              </div>
              <div class="w-full">
                <label class="text-neutral-500 text-sm mb-1 flex gap-x-1">Total Cost</label>
                <InputNumber
                  inputClass="!w-full !text-surface-600"
                  :disabled="true"
                  label="Total Cost"
                  class="w-full"
                  inputName="totalCost"
                  id="totalCost"
                  type="number"
                  v-model="totalCostAmount"
                />
              </div>
            </div>
            <FormInputText
              :viewOnly="isViewPage"
              label="Count"
              inputName="yarnCount"
              id="yarnCount"
              @blur="values.yarnCount = values.yarnCount?.trim()"
            />
          </FormSection>
        </div>
      </template>
    </Card>
  </form>
</template>